import axios, { AxiosInstance, AxiosResponse } from "axios";
import { env } from "../../configs/reexports";
import { Tenant } from "../types/tenant";
import { AuthUser } from "../types/user";
import { getIdToken, getJwtToken } from "../utils/getJwtToken";

export class AuthApiClient {
  private axiosInstance: AxiosInstance;

  constructor() {
    let envPath = "/dev";

    switch (env.name) {
      case "prod":
        envPath = "/appsprod";
        break;

      case "staging":
        envPath = "/nftstage";

        break;

      default:
        break;
    }

    this.axiosInstance = axios.create({
      baseURL: "https://" + env.stacks.webapp.domain.apiGateway + envPath,
    });
  }

  public async getUsers(): Promise<AuthUser[]> {
    try {
      const response: AxiosResponse<AuthUser[]> = await this.axiosInstance.get(
        "/users",
        { headers: await this.getHeaders() }
      );

      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || error.message);
      }

      throw new Error((error as Error).message);
    }
  }

  public async getCurentUser(): Promise<AuthUser> {
    try {
      const response: AxiosResponse<{ data: AuthUser }> =
        await this.axiosInstance.get("/users/current", {
          headers: await this.getHeaders(),
        });

      return response.data.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || error.message);
      }

      throw new Error((error as Error).message);
    }
  }

  public async getCustomers(): Promise<Tenant[]> {
    try {
      const response: AxiosResponse<Tenant[]> = await this.axiosInstance.get(
        "/tenants",
        { headers: await this.getHeaders() }
      );

      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || error.message);
      }

      throw new Error((error as Error).message);
    }
  }

  public async getCustomer(id: string): Promise<Tenant> {
    try {
      const response: AxiosResponse<Tenant> = await this.axiosInstance.get(
        "/tenant/" + id,
        { headers: await this.getHeaders() }
      );

      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || error.message);
      }

      throw new Error((error as Error).message);
    }
  }

  private async getHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const idToken = await getIdToken();
    const accessToken = await getJwtToken();

    headers["authorization"] = `Bearer ${accessToken}`;

    headers["id-token"] = idToken;

    return headers;
  }
}
